/**
 * The public claims of a token, for the log line written when a session ends.
 *
 * The token itself is a credential and never leaves the device. These are the
 * fields already signed into it and readable by anyone holding it, and each
 * answers a question the log has to answer on its own: `jti` is the backend's
 * own identifier for the record, so it is the only way to ask them what
 * happened to one specific token; `issuedAt` and `expiresAt` say whether the
 * session ended because the token ran out or while it was still valid.
 */
export interface TokenClaims {
  jti: string | null;
  issuedAt: string | null;
  expiresAt: string | null;
  /** Seconds left at the moment of reading. Negative once expired. */
  expiresInSeconds: number | null;
}

const toIso = (seconds: unknown): string | null =>
  typeof seconds === 'number' && Number.isFinite(seconds)
    ? new Date(seconds * 1000).toISOString()
    : null;

/**
 * Reads a JWT's payload without verifying it.
 *
 * Verification is the backend's job and would need its key; this only has to
 * read claims the token already carries, and it has to survive a value that is
 * not a JWT at all rather than throwing inside an error handler.
 */
const decodePayload = (token: string): Record<string, unknown> | null => {
  try {
    const payload = token.split('.')[1];
    if (!payload) {
      return null;
    }

    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      '=',
    );

    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
};

export const describeTokenClaims = (
  token: string | null | undefined,
): TokenClaims | null => {
  if (!token) {
    return null;
  }

  const payload = decodePayload(token);
  const exp = payload?.exp;

  return {
    jti: typeof payload?.jti === 'string' ? payload.jti : null,
    issuedAt: toIso(payload?.iat),
    expiresAt: toIso(exp),
    expiresInSeconds:
      typeof exp === 'number' && Number.isFinite(exp)
        ? Math.round(exp - Date.now() / 1000)
        : null,
  };
};
